import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  Plus, Trash2, Camera, Save, ChevronLeft, ChevronRight, Download, 
  Edit2, TrendingUp, TrendingDown, Minus, Package, Eye, Check, Info, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, cn } from '../lib/utils';
import { exportSalesVolumeToExcel } from './ExportExcelButton';
import { UnsavedChangesDialog, useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, Legend, Cell, LabelList
} from 'recharts';

const COLORS = ['#3B82F6', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

const GROWTH_TYPES = [
  { value: 'fixed', label: 'Mantener Volumen Fijo', icon: Minus, description: 'El volumen se mantiene igual cada año' },
  { value: 'growth', label: 'Crecimiento Anual (%)', icon: TrendingUp, description: 'Crece un porcentaje cada año' },
  { value: 'decline', label: 'Decrecimiento Anual (%)', icon: TrendingDown, description: 'Disminuye un porcentaje cada año' },
  { value: 'manual', label: 'Definir Manualmente', icon: Edit2, description: 'Define el volumen para cada año' }
];

export const SalesVolumePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject, uploadImage } = useProject();
  const { showLabels, formatChartValue } = useChartPreferences();
  const fileInputRef = useRef(null);
  
  const [products, setProducts] = useState([]);
  const [operatingMonths, setOperatingMonths] = useState([12, 12, 12, 12, 12]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [selectedProductFilter, setSelectedProductFilter] = useState('all');
  const [volumeDisplayMode, setVolumeDisplayMode] = useState('annual');
  const [initialProducts, setInitialProducts] = useState(null);
  const [initialOperatingMonths, setInitialOperatingMonths] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Estado para el producto en edición
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    id: '',
    name: '',
    photo: null,
    input_frequency: 'daily',
    base_volume: 0,
    growth_type: 'fixed',
    growth_percentage: 0,
    manual_volumes: {},
    unit_price: 0,
    unit_cost: 0,
    price_growth_type: 'inflation',
    manual_prices: {}
  });

  const years = currentProject?.specifications?.project_life_years || 5;
  const operatesSevenDays = currentProject?.specifications?.operates_seven_days ?? true;
  const daysPerYear = operatesSevenDays ? 365 : 312;

  useEffect(() => {
    const loadData = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.products) {
      setProducts(currentProject.products);
      setInitialProducts(JSON.stringify(currentProject.products));
    }
    if (currentProject?.operating_months) {
      const om = currentProject.operating_months.length >= years 
        ? currentProject.operating_months.slice(0, years)
        : [...currentProject.operating_months, ...Array(years - currentProject.operating_months.length).fill(12)];
      setOperatingMonths(om);
      setInitialOperatingMonths(JSON.stringify(om));
    } else {
      const defaultOm = Array(years).fill(12);
      setOperatingMonths(defaultOm);
      setInitialOperatingMonths(JSON.stringify(defaultOm));
    }
  }, [currentProject, years]);

  // Calcular proyección del producto actual
  const calculateProjection = (product) => {
    const projection = [];
    let volume = product.base_volume;
    const daysPerMonth = daysPerYear / 12;
    
    for (let year = 1; year <= years; year++) {
      let yearVolume;
      const opMonths = operatingMonths[year - 1] || 12;
      
      if (product.growth_type === 'fixed') {
        yearVolume = volume;
      } else if (product.growth_type === 'growth' || product.growth_type === 'percentage') {
        yearVolume = volume * Math.pow(1 + (product.growth_percentage || 0) / 100, year - 1);
      } else if (product.growth_type === 'decline') {
        yearVolume = volume * Math.pow(1 - (product.growth_percentage || 0) / 100, year - 1);
      } else if (product.growth_type === 'manual') {
        yearVolume = product.manual_volumes?.[year] ?? volume;
      } else {
        yearVolume = volume;
      }
      
      // Convertir a anual usando meses de operación
      let annualVolume;
      if (product.input_frequency === 'daily') {
        annualVolume = yearVolume * daysPerMonth * opMonths;
      } else {
        annualVolume = yearVolume * opMonths;
      }
      
      projection.push({
        year: `Año ${year}`,
        yearNum: year,
        volume: Math.round(yearVolume),
        annualVolume: Math.round(annualVolume),
        opMonths
      });
    }
    
    return projection;
  };

  const openNewProduct = () => {
    setNewProduct({
      id: crypto.randomUUID(),
      name: '',
      photo: null,
      input_frequency: 'daily',
      base_volume: 0,
      growth_type: 'fixed',
      growth_percentage: 0,
      manual_volumes: {},
      unit_price: 0,
      unit_cost: 0,
      price_growth_type: 'inflation',
      manual_prices: {}
    });
    setEditingProduct(null);
    setIsEditing(true);
  };

  const openEditProduct = (product) => {
    setNewProduct({ ...product });
    setEditingProduct(product.id);
    setIsEditing(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageData = await uploadImage(file);
      setNewProduct(prev => ({ ...prev, photo: imageData }));
      toast.success('Imagen cargada');
    } catch (err) {
      toast.error('Error al cargar imagen');
    }
  };

  const addOrUpdateProduct = () => {
    if (!newProduct.name.trim()) {
      toast.error('Por favor ingresa el nombre del producto');
      return;
    }
    if (!newProduct.base_volume || newProduct.base_volume <= 0) {
      toast.error('Por favor ingresa un volumen base mayor a 0');
      return;
    }

    if (editingProduct) {
      // Actualizar producto existente
      setProducts(prev => prev.map(p => p.id === editingProduct ? newProduct : p));
      toast.success('Producto actualizado');
    } else {
      // Agregar nuevo producto
      setProducts(prev => [...prev, newProduct]);
      toast.success('Producto agregado');
    }
    
    setIsEditing(false);
    setEditingProduct(null);
  };

  const removeProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    toast.success('Producto eliminado');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProject(projectId, { products, operating_months: operatingMonths });
      setInitialProducts(JSON.stringify(products));
      setInitialOperatingMonths(JSON.stringify(operatingMonths));
      toast.success('Productos guardados');
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = (initialProducts && JSON.stringify(products) !== initialProducts) ||
    (initialOperatingMonths && JSON.stringify(operatingMonths) !== initialOperatingMonths);

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  const goToNext = () => {
    if (!tryNavigate(`/app/project/${projectId}/pricing`)) {
      navigate(`/app/project/${projectId}/pricing`);
    }
  };

  const goToPrev = () => {
    if (!tryNavigate(`/app/project/${projectId}/financing`)) {
      navigate(`/app/project/${projectId}/financing`);
    }
  };

  // Proyección del producto en edición
  const currentProjection = calculateProjection(newProduct);

  // Datos para gráfico de resumen
  const summaryChartData = products.map((product, idx) => {
    const projection = calculateProjection(product);
    return {
      name: product.name,
      color: COLORS[idx % COLORS.length],
      ...projection.reduce((acc, p) => {
        acc[p.year] = p.annualVolume;
        return acc;
      }, {})
    };
  });

  // Datos para gráfico de líneas por año con modo de visualización
  const getLineChartData = () => {
    const daysPerMonth = daysPerYear / 12;
    return Array.from({ length: years }, (_, i) => {
      const yearData = { year: `Año ${i + 1}` };
      const opMonths = operatingMonths[i] || 12;
      products.forEach((product, idx) => {
        const projection = calculateProjection(product);
        let value = projection[i]?.annualVolume || 0;
        
        // Convertir según el modo de visualización
        if (volumeDisplayMode === 'monthly') {
          value = Math.round(value / opMonths);
        } else if (volumeDisplayMode === 'daily') {
          value = Math.round(value / (daysPerMonth * opMonths));
        }
        
        yearData[product.name] = value;
      });
      return yearData;
    });
  };
  
  const lineChartData = getLineChartData();

  const totalDailyVolume = products.reduce((sum, p) => {
    if (!p.base_volume) return sum;
    return sum + (p.input_frequency === 'daily' ? p.base_volume : p.base_volume / 30);
  }, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-volume-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 4 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Volumen de Ventas</h1>
          <p className="text-muted-foreground mt-1">
            Define los productos y su comportamiento de ventas a lo largo del proyecto.
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => exportSalesVolumeToExcel(
              products, 
              currentProject?.name || 'Proyecto',
              currentProject?.specifications?.project_life_years || 5,
              currentProject?.specifications?.operates_seven_days ? 365 : 312,
              currentProject?.specifications?.currency || 'MXN',
              operatingMonths
            )}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
          <Button onClick={openNewProduct} className="gap-2 btn-glow" data-testid="add-product-btn">
            <Plus className="w-4 h-4" />
            Agregar Producto
          </Button>
        </div>
      </div>

      <Card className="border-primary/20 bg-primary/5" data-testid="operating-months-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
              <Info className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Meses de Operación por Año</h3>
              <p className="text-xs text-muted-foreground">Define cuántos meses opera el negocio cada año (afecta todos los cálculos de volumen e ingresos)</p>
            </div>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-5 md:grid-cols-10 gap-3">
            {Array.from({ length: years }, (_, i) => (
              <div key={i}>
                <Label className="text-xs text-muted-foreground">Año {i + 1}</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={operatingMonths[i] ?? 12}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setOperatingMonths(prev => {
                      const next = [...prev];
                      next[i] = raw === '' ? '' : parseInt(raw) || '';
                      return next;
                    });
                  }}
                  onBlur={() => {
                    setOperatingMonths(prev => {
                      const next = [...prev];
                      const val = parseInt(next[i]);
                      next[i] = isNaN(val) ? 12 : Math.max(1, Math.min(12, val));
                      return next;
                    });
                  }}
                  className="mt-1 text-center font-semibold"
                  data-testid={`operating-months-year-${i + 1}`}
                />
                <p className="text-xs text-center text-muted-foreground mt-1">{operatingMonths[i] === 12 ? 'Completo' : operatingMonths[i] === '' ? '' : `${operatingMonths[i]} meses`}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="products">
            <Package className="w-4 h-4 mr-2" />
            MIS PRODUCTOS ({products.length})
          </TabsTrigger>
          <TabsTrigger value="summary">
            <Eye className="w-4 h-4 mr-2" />
            RESUMEN VISUAL
          </TabsTrigger>
        </TabsList>

        {/* TAB: MIS PRODUCTOS */}
        <TabsContent value="products" className="mt-6">
          {products.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
                <h4 className="text-lg font-semibold mb-2">No hay productos</h4>
                <p className="text-muted-foreground mb-4">
                  Agrega tu primer producto para comenzar a proyectar las ventas
                </p>
                <Button onClick={openNewProduct}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Producto
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {products.map((product, idx) => {
                const projection = calculateProjection(product);
                const GrowthIcon = GROWTH_TYPES.find(g => g.value === product.growth_type)?.icon || Minus;
                
                return (
                  <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Imagen del producto */}
                        <div 
                          className="w-32 h-32 bg-secondary flex-shrink-0 flex items-center justify-center"
                          style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20` }}
                        >
                          {product.photo ? (
                            <img src={product.photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-12 h-12" style={{ color: COLORS[idx % COLORS.length] }} />
                          )}
                        </div>
                        
                        {/* Info del producto */}
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-xl font-bold">{product.name}</h3>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <GrowthIcon className="w-4 h-4" />
                                  {product.growth_type === 'fixed' && 'Volumen Fijo'}
                                  {product.growth_type === 'growth' && `+${product.growth_percentage}% anual`}
                                  {product.growth_type === 'decline' && `-${product.growth_percentage}% anual`}
                                  {product.growth_type === 'manual' && 'Manual por año'}
                                </span>
                                <span>•</span>
                                <span>{product.input_frequency === 'daily' ? 'Venta Diaria' : 'Venta Mensual'}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditProduct(product)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => removeProduct(product.id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Mini proyección */}
                          <div className="mt-4 flex items-center gap-6">
                            <div>
                              <p className="text-xs text-muted-foreground">Volumen Base</p>
                              <p className="text-lg font-bold">
                                {formatNumber(product.base_volume)} / {product.input_frequency === 'daily' ? 'día' : 'mes'}
                              </p>
                            </div>
                            <div className="flex-1 flex items-center gap-2">
                              {projection.slice(0, 5).map((p, i) => (
                                <div key={i} className="flex-1 text-center">
                                  <p className="text-xs text-muted-foreground">Año {i + 1}</p>
                                  <p className="text-sm font-semibold tabular-nums">
                                    {formatNumber(p.annualVolume)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Summary Bar */}
          {products.length > 0 && (
            <Card className="bg-secondary/30 mt-6">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Productos</p>
                  <p className="text-2xl font-bold">{products.length} productos configurados</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase">Ventas Diarias Estimadas (Año 1)</p>
                  <p className="text-2xl font-bold">{formatNumber(totalDailyVolume)} unidades/día</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: RESUMEN VISUAL */}
        <TabsContent value="summary" className="mt-6 space-y-6">
          {products.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">Agrega productos para ver el resumen visual</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filtro Global */}
              <div className="flex justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Ver como:</span>
                  <Select value={volumeDisplayMode} onValueChange={setVolumeDisplayMode}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">Total Anual</SelectItem>
                      <SelectItem value="monthly">Promedio Mensual</SelectItem>
                      <SelectItem value="daily">Promedio Diario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select value={selectedProductFilter} onValueChange={setSelectedProductFilter}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Todos los productos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los productos</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name || 'Sin nombre'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Gráfico de barras - Evolución por año */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    Evolución de Volumen por Año 
                    <span className="text-muted-foreground font-normal text-base ml-2">
                      ({volumeDisplayMode === 'annual' ? 'Total Anual' : volumeDisplayMode === 'monthly' ? 'Promedio Mensual' : 'Promedio Diario'})
                    </span>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {volumeDisplayMode === 'annual' && 'Proyección de unidades vendidas totales por año'}
                    {volumeDisplayMode === 'monthly' && 'Proyección de unidades promedio vendidas por mes'}
                    {volumeDisplayMode === 'daily' && 'Proyección de unidades promedio vendidas por día'}
                  </p>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={lineChartData} barCategoryGap="15%" margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                      <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v, '')} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                        formatter={(value) => [formatNumber(value), volumeDisplayMode === 'annual' ? 'unidades/año' : volumeDisplayMode === 'monthly' ? 'unidades/mes' : 'unidades/día']}
                      />
                      <Legend />
                      {products
                        .filter(p => selectedProductFilter === 'all' || p.id === selectedProductFilter)
                        .map((product, idx) => (
                        <Bar 
                          key={product.id}
                          dataKey={product.name} 
                          fill={COLORS[idx % COLORS.length]}
                          radius={[4, 4, 0, 0]}
                        >
                          {showLabels && (
                            <LabelList dataKey={product.name} position="top" formatter={(v) => formatChartValue(v, '')} fill="#94A3B8" fontSize={9} />
                          )}
                        </Bar>
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Resumen por producto */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {products
                  .filter(p => selectedProductFilter === 'all' || p.id === selectedProductFilter)
                  .map((product, idx) => {
                  const projection = calculateProjection(product);
                  const year1 = projection[0]?.annualVolume || 0;
                  const yearLast = projection[projection.length - 1]?.annualVolume || 0;
                  const growth = year1 > 0 ? ((yearLast - year1) / year1 * 100) : 0;
                  // CAGR = (Final/Initial)^(1/n) - 1
                  const n = years - 1;
                  const cagr = (year1 > 0 && n > 0) ? (Math.pow(yearLast / year1, 1 / n) - 1) * 100 : 0;
                  
                  return (
                    <Card key={product.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20` }}
                          >
                            {product.photo ? (
                              <img src={product.photo} alt="" className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Package className="w-5 h-5" style={{ color: COLORS[idx % COLORS.length] }} />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{product.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {product.input_frequency === 'daily' ? 'Diario' : 'Mensual'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div className="p-2 bg-secondary/50 rounded">
                            <p className="text-xs text-muted-foreground">Año 1</p>
                            <p className="font-bold">{formatNumber(year1)}</p>
                          </div>
                          <div className="p-2 bg-secondary/50 rounded">
                            <p className="text-xs text-muted-foreground">Año {years}</p>
                            <p className="font-bold">{formatNumber(yearLast)}</p>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-center text-sm">
                          <div className={cn(
                            "font-medium",
                            growth > 0 ? "text-success" : growth < 0 ? "text-destructive" : "text-muted-foreground"
                          )}>
                            {growth > 0 ? '+' : ''}{growth.toFixed(1)}% total
                          </div>
                          <div className={cn(
                            "font-bold",
                            cagr > 0 ? "text-primary" : cagr < 0 ? "text-destructive" : "text-muted-foreground"
                          )}>
                            CAGR: {cagr > 0 ? '+' : ''}{cagr.toFixed(1)}%
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Verification Table - Volume by Year */}
              <Card className="border-primary/30">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Tabla de Verificación - Volumen por Producto/Año
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Control check de datos: Volumen base × factor de crecimiento = Volumen anual
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow className="bg-primary/10">
                          <TableHead className="font-bold text-primary">Producto</TableHead>
                          <TableHead className="font-bold text-primary">Frecuencia</TableHead>
                          <TableHead className="font-bold text-primary">Crecimiento</TableHead>
                          {Array.from({ length: years }, (_, i) => (
                            <TableHead key={i} className="text-center font-bold text-primary">
                              Año {i + 1}
                            </TableHead>
                          ))}
                          <TableHead className="text-center font-bold text-primary">Cambio Total</TableHead>
                          <TableHead className="text-center font-bold text-primary bg-primary/20">CAGR</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products
                          .filter(p => selectedProductFilter === 'all' || p.id === selectedProductFilter)
                          .map((product, pIdx) => {
                          const projection = calculateProjection(product);
                          const year1 = projection[0]?.annualVolume || 0;
                          const yearLast = projection[projection.length - 1]?.annualVolume || 0;
                          const growth = year1 > 0 ? ((yearLast - year1) / year1 * 100) : 0;
                          const n = years - 1;
                          const cagr = (year1 > 0 && n > 0) ? (Math.pow(yearLast / year1, 1 / n) - 1) * 100 : 0;
                          const GrowthIcon = GROWTH_TYPES.find(g => g.value === product.growth_type)?.icon || Minus;
                          
                          return (
                            <React.Fragment key={product.id}>
                              {/* Daily/Monthly Volume Row */}
                              <TableRow className={pIdx % 2 === 0 ? 'bg-secondary/20' : ''}>
                                <TableCell rowSpan={2} className="font-semibold border-r border-border align-middle">
                                  <div className="flex items-center gap-2">
                                    {product.photo ? (
                                      <img src={product.photo} alt="" className="w-8 h-8 rounded object-cover" />
                                    ) : (
                                      <div 
                                        className="w-8 h-8 rounded flex items-center justify-center"
                                        style={{ backgroundColor: `${COLORS[pIdx % COLORS.length]}20` }}
                                      >
                                        <Package className="w-4 h-4" style={{ color: COLORS[pIdx % COLORS.length] }} />
                                      </div>
                                    )}
                                    {product.name}
                                  </div>
                                </TableCell>
                                <TableCell rowSpan={2} className="text-muted-foreground align-middle">
                                  {product.input_frequency === 'daily' ? 'Diario' : 'Mensual'}
                                </TableCell>
                                <TableCell rowSpan={2} className="align-middle">
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <GrowthIcon className="w-4 h-4" />
                                    {product.growth_type === 'fixed' && 'Fijo'}
                                    {(product.growth_type === 'growth' || product.growth_type === 'percentage') && `+${product.growth_percentage}%`}
                                    {product.growth_type === 'decline' && `-${product.growth_percentage}%`}
                                    {product.growth_type === 'manual' && 'Manual'}
                                  </div>
                                </TableCell>
                                {projection.map((p, yIdx) => (
                                  <TableCell key={yIdx} className="text-center tabular-nums text-muted-foreground">
                                    {formatNumber(p.volume)}/{product.input_frequency === 'daily' ? 'día' : 'mes'}
                                  </TableCell>
                                ))}
                                <TableCell rowSpan={2} className={cn(
                                  "text-center font-bold",
                                  growth > 0 ? "text-success" : growth < 0 ? "text-destructive" : "text-muted-foreground"
                                )}>
                                  {growth > 0 ? '+' : ''}{growth.toFixed(1)}%
                                </TableCell>
                                <TableCell rowSpan={2} className={cn(
                                  "text-center font-bold bg-primary/10",
                                  cagr > 0 ? "text-primary" : cagr < 0 ? "text-destructive" : "text-muted-foreground"
                                )}>
                                  {cagr > 0 ? '+' : ''}{cagr.toFixed(1)}%
                                </TableCell>
                              </TableRow>
                              {/* Annual Volume Row */}
                              <TableRow className={`${pIdx % 2 === 0 ? 'bg-secondary/20' : ''} border-b-2 border-border`}>
                                {projection.map((p, yIdx) => (
                                  <TableCell key={yIdx} className="text-center tabular-nums font-semibold text-primary">
                                    {formatNumber(p.annualVolume)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                        {/* Totals Row */}
                        {selectedProductFilter === 'all' && (
                          <TableRow className="bg-primary/20 font-bold">
                            <TableCell colSpan={3} className="text-primary">TOTAL ANUAL</TableCell>
                            {Array.from({ length: years }, (_, i) => {
                              const yearTotal = products.reduce((sum, p) => {
                                const projection = calculateProjection(p);
                                return sum + (projection[i]?.annualVolume || 0);
                              }, 0);
                              return (
                                <TableCell key={i} className="text-center tabular-nums text-primary">
                                  {formatNumber(yearTotal)}
                                </TableCell>
                              );
                            })}
                            {/* CAGR for total */}
                            {(() => {
                              const year1Total = products.reduce((sum, p) => {
                                const projection = calculateProjection(p);
                                return sum + (projection[0]?.annualVolume || 0);
                              }, 0);
                              const yearLastTotal = products.reduce((sum, p) => {
                                const projection = calculateProjection(p);
                                return sum + (projection[years - 1]?.annualVolume || 0);
                              }, 0);
                              const totalGrowth = year1Total > 0 ? ((yearLastTotal - year1Total) / year1Total * 100) : 0;
                              const n = years - 1;
                              const totalCagr = (year1Total > 0 && n > 0) ? (Math.pow(yearLastTotal / year1Total, 1 / n) - 1) * 100 : 0;
                              return (
                                <>
                                  <TableCell className="text-center text-primary">{totalGrowth > 0 ? '+' : ''}{totalGrowth.toFixed(1)}%</TableCell>
                                  <TableCell className="text-center text-primary bg-primary/20">{totalCagr > 0 ? '+' : ''}{totalCagr.toFixed(1)}%</TableCell>
                                </>
                              );
                            })()}
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* DIALOG: Agregar/Editar Producto */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}
            </DialogTitle>
            <DialogDescription>
              Configura las especificaciones del producto y su comportamiento de ventas
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Columna izquierda: Configuración */}
            <div className="space-y-4">
              {/* Foto y nombre */}
              <div className="flex items-start gap-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl bg-secondary flex-shrink-0 flex items-center justify-center overflow-hidden hover:bg-secondary/80 transition-colors border-2 border-dashed border-border hover:border-primary"
                >
                  {newProduct.photo ? (
                    <img src={newProduct.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-6 h-6 text-muted-foreground" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <div className="flex-1">
                  <Label>Nombre del Producto</Label>
                  <Input
                    placeholder="Ej: Café Americano, Membresía Mensual..."
                    value={newProduct.name}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                    data-testid="product-name-input"
                  />
                </div>
              </div>

              {/* Frecuencia de venta */}
              <div>
                <Label>¿Cómo se vende este producto?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant={newProduct.input_frequency === 'daily' ? 'default' : 'outline'}
                    onClick={() => setNewProduct(prev => ({ ...prev, input_frequency: 'daily' }))}
                    className="justify-start"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Venta Diaria
                  </Button>
                  <Button
                    type="button"
                    variant={newProduct.input_frequency === 'monthly' ? 'default' : 'outline'}
                    onClick={() => setNewProduct(prev => ({ ...prev, input_frequency: 'monthly' }))}
                    className="justify-start"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Venta Mensual
                  </Button>
                </div>
              </div>

              {/* Volumen base */}
              <div>
                <Label>
                  Volumen Base ({newProduct.input_frequency === 'daily' ? 'unidades por día' : 'unidades por mes'})
                </Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newProduct.base_volume || ''}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, base_volume: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 text-lg"
                  data-testid="volume-input"
                />
              </div>

              {/* Tipo de crecimiento */}
              <div>
                <Label>Comportamiento a lo largo del proyecto</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {GROWTH_TYPES.map(type => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={newProduct.growth_type === type.value ? 'default' : 'outline'}
                      onClick={() => setNewProduct(prev => ({ 
                        ...prev, 
                        growth_type: type.value,
                        growth_percentage: type.value === 'fixed' ? 0 : prev.growth_percentage
                      }))}
                      className="justify-start h-auto py-3 flex-col items-start"
                    >
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        <span>{type.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-normal mt-1">{type.description}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Porcentaje de crecimiento/decrecimiento */}
              {(newProduct.growth_type === 'growth' || newProduct.growth_type === 'decline') && (
                <div>
                  <Label>
                    Porcentaje de {newProduct.growth_type === 'growth' ? 'crecimiento' : 'decrecimiento'} anual
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      placeholder="0"
                      value={newProduct.growth_percentage || ''}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, growth_percentage: parseFloat(e.target.value) || 0 }))}
                      className="w-24"
                    />
                    <span className="text-muted-foreground">% anual</span>
                  </div>
                </div>
              )}

              {/* Volúmenes manuales por año */}
              {newProduct.growth_type === 'manual' && (
                <div>
                  <Label>Volumen por Año ({newProduct.input_frequency === 'daily' ? 'diario' : 'mensual'})</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {Array.from({ length: years }, (_, i) => (
                      <div key={i}>
                        <p className="text-xs text-muted-foreground mb-1">Año {i + 1}</p>
                        <Input
                          type="number"
                          placeholder={String(newProduct.base_volume || 0)}
                          value={newProduct.manual_volumes?.[i + 1] ?? ''}
                          onChange={(e) => setNewProduct(prev => ({
                            ...prev,
                            manual_volumes: {
                              ...prev.manual_volumes,
                              [i + 1]: parseFloat(e.target.value) || prev.base_volume
                            }
                          }))}
                          className="text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Columna derecha: Proyección */}
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Vista Previa de Proyección
                </Label>
                <p className="text-sm text-muted-foreground">
                  Así se comportará este producto durante los {years} años del proyecto
                </p>
              </div>

              {newProduct.base_volume > 0 ? (
                <>
                  <Card className="bg-secondary/30">
                    <CardContent className="p-4">
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={currentProjection} margin={{ top: 15, right: 5, left: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                          <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} />
                          <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => formatChartValue(v, '')} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                            formatter={(value) => formatNumber(value)}
                          />
                          <Bar dataKey="annualVolume" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Unidades/Año" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Tabla de proyección */}
                  <div className="bg-secondary/20 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground uppercase mb-2">Detalle por Año</p>
                    <div className="space-y-1">
                      {currentProjection.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-border/30 last:border-0">
                          <span>{p.year}</span>
                          <span className="tabular-nums">
                            <span className="text-muted-foreground mr-2">
                              {formatNumber(p.volume)}/{newProduct.input_frequency === 'daily' ? 'día' : 'mes'}
                            </span>
                            <span className="font-semibold">{formatNumber(p.annualVolume)} total</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center text-muted-foreground">
                    Ingresa un volumen base para ver la proyección
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={addOrUpdateProduct} className="gap-2">
              <Check className="w-4 h-4" />
              {editingProduct ? 'Guardar Cambios' : 'Agregar Producto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goToPrev}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={goToNext} data-testid="continue-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />
    </div>
  );
};
